import React from 'react';
import { type WaitingRoomV2VisitListItemDoctorApiOutContract } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas';
import {
  getSkipStartConfirmation,
  setSkipStartConfirmation,
} from '../utils/startConfirmationPreference';

type StartVisit = (visit: WaitingRoomV2VisitListItemDoctorApiOutContract) => void | Promise<void>;

export const useAsyncVisitStart = (
  startVisit: StartVisit,
  guardAgainstOtherTab?: () => Promise<boolean>,
) => {
  const [pendingVisit, setPendingVisit] = React.useState<
    WaitingRoomV2VisitListItemDoctorApiOutContract | undefined
  >(undefined);
  const [isStarting, setIsStarting] = React.useState(false);
  // Synchronous in-flight lock: prevents a rapid double-click from firing two claims in the
  // window before the list refetch flips the row to in-progress. The state mirror drives the
  // disabled buttons; the ref is the source of truth for the guard (no stale-closure race).
  const startingRef = React.useRef(false);

  const runStart = React.useCallback(
    async (visit: WaitingRoomV2VisitListItemDoctorApiOutContract) => {
      if (startingRef.current) return;
      startingRef.current = true;
      setIsStarting(true);
      try {
        await startVisit(visit);
      } finally {
        startingRef.current = false;
        setIsStarting(false);
      }
    },
    [startVisit],
  );

  const requestStart = React.useCallback(
    async (visit: WaitingRoomV2VisitListItemDoctorApiOutContract) => {
      if (startingRef.current) return;
      // Skipping the confirmation goes straight to startVisit, which runs its own tab check.
      if (getSkipStartConfirmation()) {
        await runStart(visit);
        return;
      }
      // Otherwise check for another tab BEFORE showing the confirmation popup, so the error
      // toast fires first rather than the provider seeing "Ready to start?" then an error.
      if (guardAgainstOtherTab && (await guardAgainstOtherTab())) return;
      setPendingVisit(visit);
    },
    [runStart, guardAgainstOtherTab],
  );

  const confirmStart = React.useCallback(
    (dontShowAgain: boolean) => {
      if (dontShowAgain) setSkipStartConfirmation(true);
      const visit = pendingVisit;
      setPendingVisit(undefined);
      if (visit) void runStart(visit);
    },
    [pendingVisit, runStart],
  );

  const cancel = React.useCallback(() => setPendingVisit(undefined), []);

  return {
    isConfirmOpen: pendingVisit !== undefined,
    isStarting,
    requestStart,
    confirmStart,
    cancel,
  };
};
